import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { SubscriptionContext } from "contexts/Subscription";
import {
  ReactNode,
  isValidElement,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useInView } from "react-intersection-observer";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { UpgradeDialogSource } from "schemas/dashboard";

import { AI_DRAFT_CREDITS_TOOLTIP } from "constants/influencer";
import { MERGE_TAGS } from "constants/templates";
import { fetcherAuth } from "utils/api";
import { ON_BEHALF_OF_TOKEN } from "utils/localStorage";
import { getInternalPath } from "utils/navigation";
import { useSubscription } from "utils/useSubscription";

import SuggestedPrompts from "./SuggestedPrompts";
import { PromptPage, SuggestedPrompt, useSuggestedPrompts } from "./api";
import styles from "./styles";

const SUGGESTED_PROMPTS_IDLE_THRESHOLD_MS = 10 * 60 * 1000;
const MAX_SUGGESTED_PROMPTS = 5;
const MESSAGES_PER_PAGE = 50;

const TOOL_STATUS_LABELS: Record<string, string> = {
  get_activity_overview: "Reviewing your activity",
  get_template_details: "Reviewing your templates",
  list_templates: "Looking up your templates",
  get_campaign_stats: "Reviewing your campaigns",
  get_niche_breakdown: "Analyzing your niches",
  get_action_item_counts: "Checking your inbox",
  list_pending_action_items: "Checking pending replies",
  get_check_in_count: "Checking your check-ins",
  list_pending_check_ins: "Checking your check-ins",
  draft_reply_for_action_item: "Drafting a reply",
  get_template_link: "Finding the template",
  get_action_item_link: "Finding the thread",
  list_niches: "Reviewing your niches",
  get_niche_link: "Finding the niche",
};

function toolStatusLabel(name: string): string {
  return TOOL_STATUS_LABELS[name] ?? "Working on it";
}

// The model sometimes outputs `\/` instead of `/` inside markdown link URLs
// (a JSON-escape leak). Normalize before rendering so links resolve.
function normalizeAssistantText(text: string): string {
  return text.replace(/\\\//g, "/");
}

// Convert the raw plain-text body of a markdown code block into the HTML
// shape the rich-text editor (TipTap) actually produces when you paste the
// same text into it. We mirror two pieces of the editor's pipeline:
//   1. CleanPaste.clipboardTextParser: split on `\n`, each line becomes its
//      own <p>. Empty lines stay as empty <p> spacers (the editor styles
//      `p:empty::after` to give them visible height — that's where the
//      blank line of vertical spacing between paragraphs comes from).
//   2. MergeTag.addPasteRules: turn `{First Name}` / `{Brand Name}` into the
//      same <span data-type="merge-tag" …> structure the node renders, so
//      they show up as personalization chips instead of literal text.
function plainTextToTemplateHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const mergeTagRe = new RegExp(`\\{(${MERGE_TAGS.join("|")})\\}`, "g");
  return text
    .replace(/\r\n?/g, "\n")
    .split("\n")
    .map((line) => {
      if (!line) return "<p></p>";
      const inner = escape(line).replace(
        mergeTagRe,
        (_match, label) =>
          `<span class="merge-tag" data-type="merge-tag" data-id="${label}" data-label="${label}">{${label}}</span>`,
      );
      return `<p>${inner}</p>`;
    })
    .join("");
}

// For messages that contain a `?openCreate=` template-create deep link AND a
// fenced code block, pair every create link with the FIRST code block in the
// message (regardless of position). The pair lets the link click handler
// pre-fill the template editor with the code-block body via
// OutreachTemplatesContext.
function buildTemplatePrefillMap(text: string): Map<string, string> {
  const map = new Map<string, string>();
  const codeBlockRe = /```[^\n]*\n?([\s\S]*?)```/g;
  // Allow optional whitespace inside the `( ... )` markdown link parens —
  // models sometimes emit `[label]( /dashboard/...)` with a leading space.
  const linkRe =
    /\[[^\]]*\]\(\s*(\/dashboard\/templates\?openCreate=[^)\s]*)\s*\)/g;
  const firstCodeMatch = codeBlockRe.exec(text);
  if (!firstCodeMatch) return map;
  const body = firstCodeMatch[1].trim();
  if (!body) return map;
  const html = plainTextToTemplateHtml(body);
  let linkMatch: RegExpExecArray | null;
  while ((linkMatch = linkRe.exec(text)) !== null) {
    map.set(linkMatch[1], html);
  }
  return map;
}

const API_BASE_URL = process.env.REACT_APP_API_URL ?? "";

type ChatMessage = {
  id: string;
  role: "user" | "assistant" | "system" | "tool";
  text: string;
  createdAt: number;
};

type ServerMessage = {
  id: number;
  role: string;
  text: string | null;
  item: Record<string, unknown>;
  created_at: string | null;
};

function derivePromptPage(pathname: string): PromptPage {
  if (pathname.startsWith("/dashboard/campaigns")) return "campaigns";
  if (pathname.startsWith("/dashboard/templates")) return "templates";
  if (pathname.startsWith("/dashboard/inbox")) return "inbox";
  if (pathname.startsWith("/dashboard/brands")) return "discover";
  return "home";
}

function isAdminImpersonating(): boolean {
  if (typeof window === "undefined") return false;
  return Boolean(sessionStorage.getItem(ON_BEHALF_OF_TOKEN));
}

const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

export function isAssistantAvailable(): boolean {
  return isAdminImpersonating() || IS_DEVELOPMENT;
}

function nodeToText(node: ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeToText).join("");
  if (isValidElement(node)) {
    return nodeToText((node.props as { children?: ReactNode }).children);
  }
  return "";
}

const CodeBlock = ({ children }: { children: ReactNode }) => {
  const [copied, setCopied] = useState(false);
  const text = nodeToText(children);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore — clipboard write can fail in non-secure contexts
    }
  };

  return (
    <Box sx={{ position: "relative", my: 1 }}>
      <Button
        size="small"
        onClick={handleCopy}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          minWidth: 0,
          px: 1,
          py: 0.25,
          fontSize: 11,
          textTransform: "none",
          color: copied ? "#008236" : "primary.main",
          backgroundColor: "rgba(255,255,255,0.9)",
          border: "1px solid rgba(1,67,57,0.1)",
          "&:hover": {
            backgroundColor: "rgba(255,255,255,1)",
          },
        }}
      >
        <Box
          component="i"
          className={copied ? "fa-regular fa-check" : "fa-regular fa-copy"}
          sx={{ fontSize: 11, mr: 0.5 }}
        />
        {copied ? "Copied" : "Copy"}
      </Button>
      <Box
        component="pre"
        sx={{
          backgroundColor: "#fafaf6",
          border: "1px solid rgba(1,67,57,0.1)",
          borderRadius: 1,
          p: 1.5,
          pr: 6,
          m: 0,
          fontSize: 12,
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowX: "auto",
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const TypingDots = () => (
  <Box sx={styles.typingDots} aria-label="Assistant is typing">
    <span />
    <span />
    <span />
  </Box>
);

const LoadingSkeleton = () => (
  <Box sx={styles.loadingStack}>
    <Skeleton
      animation="wave"
      variant="rounded"
      height={36}
      sx={styles.loadingBubbleUser}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={120}
      sx={styles.loadingBubbleAssistant}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={48}
      sx={styles.loadingBubbleUser}
    />
    <Skeleton
      animation="wave"
      variant="rounded"
      height={88}
      sx={styles.loadingBubbleAssistant}
    />
  </Box>
);

const AssistantPreview = () => {
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    setDiscoverTab,
    assistantOpenContext,
    setAssistantOpenContext,
    assistantOpenedAt,
  } = useContext(DiscoverViewContext);
  const { subscription, setUpgradeDialogSource } =
    useContext(SubscriptionContext);
  const { setCurrentTemplateBody } = useContext(OutreachTemplatesContext);
  const { decreaseChatCount } = useSubscription();
  const available = useMemo(isAssistantAvailable, []);
  const location = useLocation();
  const navigate = useNavigate();
  const urlPage = useMemo(
    () => derivePromptPage(location.pathname),
    [location.pathname],
  );
  const currentPage = assistantOpenContext?.promptPage ?? urlPage;
  const currentEntity = assistantOpenContext?.entity ?? null;

  const [conversationId, setConversationId] = useState<number | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeToolName, setActiveToolName] = useState<string | null>(null);
  const [messagesPage, setMessagesPage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [pageNavigatedAt, setPageNavigatedAt] = useState(0);
  const [dynamicFollowups, setDynamicFollowups] = useState<
    SuggestedPrompt[] | null
  >(null);
  const [dynamicFollowupsLoading, setDynamicFollowupsLoading] = useState(false);
  const [dynamicFollowupsPage, setDynamicFollowupsPage] = useState<
    string | null
  >(null);
  // `streaming` covers the whole SSE stream including the sidecar follow-up
  // generation. `replyComplete` flips true once `message_completed` arrives so
  // we can hide the in-bubble typing dots while the sidecar is still running.
  const [replyComplete, setReplyComplete] = useState(false);
  const streamControllerRef = useRef<AbortController | null>(null);
  const previousUrlPageRef = useRef(urlPage);
  const streamStartedAtPageRef = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const isAtBottomRef = useRef(true);
  const skipPromptScrollRef = useRef(false);
  const [topSentinelRef, isTopSentinelVisible] = useInView({
    root: scrollRef.current,
    threshold: 0,
  });

  // When the user navigates while the assistant is open, drop any stale
  // entity / page context (the original CTA's campaign no longer applies on
  // a different page) and bump a timestamp so the prompts re-surface for the
  // new page even mid-conversation. Prior turn's follow-up chips stay around
  // and get merged into the new page's "Try asking" list (see suggestedPrompts).
  useEffect(() => {
    if (previousUrlPageRef.current === urlPage) return;
    previousUrlPageRef.current = urlPage;
    setPageNavigatedAt(Date.now());
    skipPromptScrollRef.current = true;
    if (assistantOpenContext && assistantOpenContext.promptPage !== urlPage) {
      setAssistantOpenContext(null);
    }
  }, [urlPage, assistantOpenContext, setAssistantOpenContext]);

  const isLoadingCredits = subscription === null;
  const remainingCredits = subscription?.remainingChatMessages ?? 0;
  const isPaidUser = Boolean(currentOrg?.isPaidUser);
  const outOfCredits = !isLoadingCredits && remainingCredits <= 0;

  const lastMessageAt = messages[messages.length - 1]?.createdAt;
  const sidecarActive = streaming && replyComplete;
  const followupsPresent =
    (sidecarActive || !streaming) &&
    (dynamicFollowupsLoading ||
      (dynamicFollowups !== null && dynamicFollowups.length > 0));
  // Followups belong to a single page (the one the turn started on). When the
  // user navigates, they become "stale" and we merge them with the new page's
  // chips rather than letting them hide the page-based prompts.
  const followupsFromCurrentPage =
    followupsPresent &&
    dynamicFollowupsPage !== null &&
    dynamicFollowupsPage === urlPage;
  const hasDynamicFollowups = followupsPresent && followupsFromCurrentPage;
  const shouldShowStaticPrompts =
    !bootstrapping &&
    !streaming &&
    conversationId !== null &&
    !hasDynamicFollowups &&
    (assistantOpenContext !== null ||
      messages.length === 0 ||
      pageNavigatedAt > (lastMessageAt ?? 0) ||
      (lastMessageAt !== undefined &&
        Date.now() - lastMessageAt > SUGGESTED_PROMPTS_IDLE_THRESHOLD_MS));
  const shouldShowSuggestedPrompts =
    followupsPresent || shouldShowStaticPrompts;

  const { prompts: staticPrompts, loading: staticPromptsLoading } =
    useSuggestedPrompts(
      available && shouldShowStaticPrompts ? currentPage : null,
      messages.length,
      currentEntity,
    );

  const suggestedPrompts = useMemo(() => {
    if (!followupsPresent) return staticPrompts.slice(0, MAX_SUGGESTED_PROMPTS);
    const followups = dynamicFollowups ?? [];
    if (followupsFromCurrentPage)
      return followups.slice(0, MAX_SUGGESTED_PROMPTS);
    const seen = new Set<string>();
    const merged: SuggestedPrompt[] = [];
    for (const p of followups) {
      if (seen.has(p.label)) continue;
      seen.add(p.label);
      merged.push(p);
      if (merged.length >= MAX_SUGGESTED_PROMPTS) return merged;
    }
    for (const p of staticPrompts) {
      if (seen.has(p.label)) continue;
      seen.add(p.label);
      merged.push(p);
      if (merged.length >= MAX_SUGGESTED_PROMPTS) return merged;
    }
    return merged;
  }, [
    followupsPresent,
    followupsFromCurrentPage,
    dynamicFollowups,
    staticPrompts,
  ]);
  const suggestedPromptsLoading = followupsFromCurrentPage
    ? dynamicFollowupsLoading
    : !followupsPresent
      ? staticPromptsLoading
      : dynamicFollowupsLoading || staticPromptsLoading;

  useEffect(() => {
    if (!available || !currentOrg?.id || conversationId !== null) return;
    let cancelled = false;
    (async () => {
      setBootstrapping(true);
      setError(null);
      try {
        const { conversation: conv } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations`,
          "POST",
        );
        if (cancelled || !conv?.id) return;
        const { messages: serverMessages } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations/${conv.id}?page=1&per_page=${MESSAGES_PER_PAGE}`,
          "GET",
        );
        if (cancelled) return;
        const rawMessages = serverMessages || [];
        const mapped = rawMessages
          .map(serverMessageToChatMessage)
          .filter(Boolean) as ChatMessage[];
        setConversationId(conv.id);
        setMessages(mapped);
        setMessagesPage(1);
        setHasMoreMessages(rawMessages.length >= MESSAGES_PER_PAGE);
        const cachedChips = pickCachedFollowupChips(conv, mapped);
        if (cachedChips) {
          setDynamicFollowups(cachedChips);
          // Cached chips' origin page is unknown — treat as stale so they
          // merge with the current page's static prompts.
          setDynamicFollowupsPage(null);
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message || "Failed to load");
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [available, currentOrg?.id, conversationId, getToken]);

  // Auto-scroll to bottom when the newest message changes (new send, new
  // streamed reply, or initial bootstrap). Prepending older history doesn't
  // change the newest id, so the user's scroll position is preserved.
  const newestMessageId = messages[messages.length - 1]?.id;
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [newestMessageId, streaming]);

  // When suggested prompts appear (e.g. after opening from a CTA), scroll the
  // chat down so the "Try asking" chips are in view. Also re-fire when the
  // skeletons get replaced by real chips, since the content height changes
  // and the taller chips can otherwise be cut off below the fold.
  const hasSuggestedPrompts =
    shouldShowSuggestedPrompts &&
    (suggestedPromptsLoading || suggestedPrompts.length > 0);
  useEffect(() => {
    if (!hasSuggestedPrompts) return;
    if (skipPromptScrollRef.current) return;
    if (!isAtBottomRef.current) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [hasSuggestedPrompts, suggestedPromptsLoading, suggestedPrompts.length]);

  // Each time the user opens the assistant via a "Start AI Chat" CTA, snap
  // back to the bottom — covers the case where the panel was already open
  // and scrolled up to older history.
  useEffect(() => {
    if (!assistantOpenedAt) return;
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, [assistantOpenedAt]);

  useEffect(() => {
    if (
      !isTopSentinelVisible ||
      !hasMoreMessages ||
      loadingOlder ||
      bootstrapping ||
      conversationId === null ||
      !currentOrg?.id
    ) {
      return;
    }
    const container = scrollRef.current;
    const distanceFromBottom = container
      ? container.scrollHeight - container.scrollTop
      : 0;
    setLoadingOlder(true);
    let cancelled = false;
    (async () => {
      const nextPage = messagesPage + 1;
      try {
        const { messages: olderRaw } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/assistant/conversations/${conversationId}?page=${nextPage}&per_page=${MESSAGES_PER_PAGE}`,
          "GET",
        );
        if (cancelled) return;
        const olderRawArr = olderRaw || [];
        const older = olderRawArr
          .map(serverMessageToChatMessage)
          .filter(Boolean) as ChatMessage[];
        setMessagesPage(nextPage);
        if (olderRawArr.length < MESSAGES_PER_PAGE) setHasMoreMessages(false);
        if (older.length > 0) {
          setMessages((prev) => [...older, ...prev]);
          requestAnimationFrame(() => {
            if (container) {
              container.scrollTop = container.scrollHeight - distanceFromBottom;
            }
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError((e as Error).message || "Failed to load earlier messages");
        }
      } finally {
        setLoadingOlder(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // `loadingOlder` and `messagesPage` are read inside this effect but
    // intentionally NOT in the dep array. Listing them would cause the
    // effect's own state writes (setLoadingOlder(true), setMessagesPage)
    // to trip the cleanup function
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isTopSentinelVisible,
    hasMoreMessages,
    bootstrapping,
    conversationId,
    currentOrg?.id,
  ]);

  if (!available) return null;

  const closeDialog = () => setDiscoverTab(null);

  const handleBuyMore = () => {
    setUpgradeDialogSource(
      UpgradeDialogSource.hits_personalized_ai_messages_limit,
    );
  };

  const sendMessage = () => sendMessageWithText(input.trim());

  const sendMessageWithText = async (text: string) => {
    if (!text || !currentOrg?.id || conversationId === null) return;
    if (outOfCredits) {
      handleBuyMore();
      return;
    }
    // If a prior turn's stream is still in flight (e.g. user sent again while
    // the sidecar follow-ups were still loading), cancel it so the two don't
    // overlap.
    streamControllerRef.current?.abort();
    const controller = new AbortController();
    streamControllerRef.current = controller;
    setInput("");
    setError(null);
    setActiveToolName(null);
    setAssistantOpenContext(null);
    setDynamicFollowups(null);
    setDynamicFollowupsLoading(false);
    setDynamicFollowupsPage(null);
    streamStartedAtPageRef.current = urlPage;
    skipPromptScrollRef.current = false;
    setReplyComplete(false);
    const now = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: `user-${now}`, role: "user", text, createdAt: now },
    ]);
    setStreaming(true);

    let assistantText = "";
    let creditDecremented = false;
    const assistantId = `assistant-${now}`;
    setMessages((prev) => [
      ...prev,
      { id: assistantId, role: "assistant", text: "", createdAt: Date.now() },
    ]);

    try {
      const token =
        sessionStorage.getItem(ON_BEHALF_OF_TOKEN) || (await getToken());
      const res = await fetch(
        `${API_BASE_URL}/api/organization/${currentOrg.id}/assistant/conversations/${conversationId}/messages`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ content: text, page: urlPage }),
          signal: controller.signal,
        },
      );
      if (!res.ok || !res.body) {
        const body = await res.text();
        throw new Error(`HTTP ${res.status}: ${body || "no body"}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const frames = buffer.split("\n\n");
        buffer = frames.pop() || "";
        for (const frame of frames) {
          if (!frame.trim()) continue;
          const lines = frame.split("\n");
          let event = "message";
          let data = "";
          for (const line of lines) {
            if (line.startsWith("event: ")) event = line.slice(7);
            else if (line.startsWith("data: ")) data += line.slice(6);
          }
          let parsed: any = null;
          try {
            parsed = data ? JSON.parse(data) : null;
          } catch {
            continue;
          }
          if (event === "message_delta" || event === "message_completed") {
            const chunk = parsed?.text || "";
            if (!chunk) continue;
            setActiveToolName(null);
            if (event === "message_completed") {
              assistantText = chunk;
            } else {
              assistantText += chunk;
            }
            const finalText = assistantText;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantId ? { ...m, text: finalText } : m,
              ),
            );
            if (event === "message_completed") {
              setReplyComplete(true);
              if (!creditDecremented) {
                creditDecremented = true;
                decreaseChatCount();
              }
            }
          } else if (event === "tool_call_started") {
            setActiveToolName(parsed?.tool_name || null);
          } else if (event === "tool_call_finished") {
            setActiveToolName(null);
          } else if (event === "suggested_followups_loading") {
            setDynamicFollowupsLoading(true);
          } else if (event === "suggested_followups") {
            setDynamicFollowupsLoading(false);
            const chips = Array.isArray(parsed?.chips) ? parsed.chips : [];
            setDynamicFollowups(chips.length > 0 ? chips : null);
            setDynamicFollowupsPage(streamStartedAtPageRef.current);
          } else if (event === "error") {
            setError(parsed?.message || "Stream error");
          }
        }
      }
    } catch (e) {
      if (controller.signal.aborted) return;
      setError((e as Error).message || "Send failed");
    } finally {
      // Only flip the spinner off if this is still the active stream — a newer
      // send may have already taken over.
      if (streamControllerRef.current === controller) {
        streamControllerRef.current = null;
        setStreaming(false);
        setActiveToolName(null);
      }
    }
  };

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        <Grid container justifyContent="space-between" alignItems="center">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              component="i"
              className="fa-regular fa-comments"
              sx={styles.headerIcon}
            />
            <Typography sx={styles.headerTitle}>Assistant</Typography>
          </Box>
          <IconButton sx={styles.close} onClick={closeDialog}>
            <Box component="i" className="fa-regular fa-xmark" />
          </IconButton>
        </Grid>
        <Box sx={styles.creditsBanner}>
          <Typography sx={styles.creditsText}>
            {isLoadingCredits
              ? "Loading credits…"
              : `${remainingCredits} credit${
                  remainingCredits === 1 ? "" : "s"
                } left`}
            <Tooltip
              slotProps={{
                tooltip: {
                  sx: {
                    color: "black",
                    backgroundColor: "white",
                    p: 1,
                    boxShadow: 1,
                  },
                },
              }}
              title={AI_DRAFT_CREDITS_TOOLTIP}
              enterTouchDelay={0}
            >
              <Box
                component="i"
                className="fa-solid fa-circle-info"
                sx={styles.creditsInfoIcon}
              />
            </Tooltip>
          </Typography>
          {!isPaidUser && (
            <Button
              size="small"
              disableElevation
              onClick={handleBuyMore}
              sx={styles.buyMoreButton}
            >
              Buy more
            </Button>
          )}
        </Box>
      </Box>
      <Box
        ref={scrollRef}
        sx={styles.messages}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          isAtBottomRef.current =
            el.scrollHeight - el.scrollTop - el.clientHeight < 50;
        }}
      >
        {bootstrapping && <LoadingSkeleton />}
        {hasMoreMessages && !bootstrapping && (
          <Box ref={topSentinelRef} sx={{ height: 1 }} />
        )}
        {loadingOlder && <LoadingSkeleton />}
        {messages.length === 0 && !bootstrapping && (
          <Typography variant="body2" color="text.secondary">
            Ask me anything about your outreach.
          </Typography>
        )}
        {messages.map((m, idx) => {
          const isAssistant = m.role === "assistant";
          const isLast = idx === messages.length - 1;
          const isActivelyStreaming =
            streaming && isAssistant && isLast && !replyComplete;
          const templatePrefillByHref = isAssistant
            ? buildTemplatePrefillMap(normalizeAssistantText(m.text || ""))
            : new Map<string, string>();
          return (
            <Box
              key={m.id}
              sx={
                m.role === "user" ? styles.userBubble : styles.assistantBubble
              }
            >
              {isAssistant ? (
                <Box sx={styles.assistantText}>
                  {m.text ? (
                    <>
                      <Box
                        sx={{
                          "& p": { m: 0, my: 0.25 },
                          "& ul, & ol": {
                            m: 0,
                            whiteSpace: "normal",
                            paddingLeft: "1.25em",
                          },
                          "& li": { marginBottom: 0.5 },
                          "& a": styles.assistantLink,
                        }}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            pre: ({ children }) => (
                              <CodeBlock>{children}</CodeBlock>
                            ),
                            a: ({ node, children, href, ...props }) => {
                              const internalPath =
                                typeof href === "string"
                                  ? getInternalPath(href)
                                  : null;
                              if (internalPath) {
                                return (
                                  <a
                                    {...props}
                                    href={internalPath}
                                    onClick={(e) => {
                                      if (
                                        e.metaKey ||
                                        e.ctrlKey ||
                                        e.shiftKey ||
                                        e.altKey
                                      ) {
                                        return;
                                      }
                                      e.preventDefault();
                                      const prefill =
                                        templatePrefillByHref.get(
                                          internalPath,
                                        ) ??
                                        templatePrefillByHref.get(
                                          href as string,
                                        );
                                      if (prefill) {
                                        setCurrentTemplateBody(prefill);
                                      }
                                      navigate(internalPath);
                                    }}
                                  >
                                    {children}
                                  </a>
                                );
                              }
                              return (
                                <a
                                  {...props}
                                  href={href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {children}
                                </a>
                              );
                            },
                          }}
                        >
                          {normalizeAssistantText(m.text)}
                        </ReactMarkdown>
                      </Box>
                      {isActivelyStreaming && (
                        <Box component="span" sx={styles.inlineTyping}>
                          <TypingDots />
                        </Box>
                      )}
                    </>
                  ) : isActivelyStreaming ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <TypingDots />
                      {activeToolName && (
                        <Typography
                          variant="body2"
                          sx={{ color: "#4A5565", fontStyle: "italic" }}
                        >
                          {toolStatusLabel(activeToolName)}…
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                </Box>
              ) : (
                <Box
                  sx={{
                    "& p": { m: 0, my: 0.25 },
                    "& a": styles.userLink,
                  }}
                >
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node, children, href, ...props }) => {
                        const internalPath =
                          typeof href === "string"
                            ? getInternalPath(href)
                            : null;
                        if (internalPath) {
                          return (
                            <a
                              {...props}
                              href={internalPath}
                              onClick={(e) => {
                                if (
                                  e.metaKey ||
                                  e.ctrlKey ||
                                  e.shiftKey ||
                                  e.altKey
                                ) {
                                  return;
                                }
                                e.preventDefault();
                                navigate(internalPath);
                              }}
                            >
                              {children}
                            </a>
                          );
                        }
                        return (
                          <a
                            {...props}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {children}
                          </a>
                        );
                      },
                    }}
                  >
                    {m.text}
                  </ReactMarkdown>
                </Box>
              )}
            </Box>
          );
        })}
        {error && (
          <Paper variant="outlined" sx={{ p: 1.5, borderColor: "error.main" }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Paper>
        )}
        {shouldShowSuggestedPrompts && (
          <Box sx={{ pb: 3 }}>
            <SuggestedPrompts
              prompts={suggestedPrompts}
              loading={suggestedPromptsLoading}
              onPick={sendMessageWithText}
            />
          </Box>
        )}
      </Box>
      <Box sx={styles.input}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask the assistant…"
          value={input}
          disabled={
            (streaming && !replyComplete) ||
            bootstrapping ||
            conversationId === null
          }
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        <IconButton
          onClick={sendMessage}
          disabled={
            !input.trim() ||
            (streaming && !replyComplete) ||
            bootstrapping ||
            conversationId === null
          }
          sx={styles.sendButton}
        >
          <Box
            component="i"
            className="fa-solid fa-paper-plane-top"
            sx={styles.sendIcon}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

type CachedConversation = {
  id: number;
  last_followup_chips?: SuggestedPrompt[] | null;
  last_followup_chips_at?: string | null;
};

// Decide whether to re-seed the "Try asking" chips with the sidecar's last
// output from a previous turn. Show them only when (a) chips exist, (b) the
// stored timestamp is within SUGGESTED_PROMPTS_IDLE_THRESHOLD_MS, and (c) the
// last message on file is from the assistant — otherwise the chips refer to
// an earlier turn the user has already moved past.
function pickCachedFollowupChips(
  conv: CachedConversation,
  messages: ChatMessage[],
): SuggestedPrompt[] | null {
  const chips = conv.last_followup_chips;
  const at = conv.last_followup_chips_at;
  if (!chips || chips.length === 0 || !at) return null;
  const ts = Date.parse(at);
  if (Number.isNaN(ts)) return null;
  if (Date.now() - ts > SUGGESTED_PROMPTS_IDLE_THRESHOLD_MS) return null;
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return null;
  return chips;
}

function serverMessageToChatMessage(m: ServerMessage): ChatMessage | null {
  if (!m.text) return null;
  const createdAt = m.created_at ? Date.parse(m.created_at) : Date.now();
  return {
    id: `server-${m.id}`,
    role:
      m.role === "user" || m.role === "assistant" || m.role === "tool"
        ? (m.role as "user" | "assistant" | "tool")
        : "system",
    text: m.text,
    createdAt: Number.isNaN(createdAt) ? Date.now() : createdAt,
  };
}

export default AssistantPreview;
export { AssistantPreview };
